import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';

@Component({
  selector: 'variables-editor',
  template: `
    <div class="variables-editor variables-editor-wrapper">
      <div [ngSwitch]="type">
        <div class="variables-editor-headers" *ngSwitchCase="'window'">
          {{ 'variables-editor.labels.window' | translate }}
        </div>
        <div class="variables-editor-headers" *ngSwitchCase="'select'" [hidden]="items.length === 0">
          <div class="variables-editor-header variables-variable">
            {{ 'variables-editor.labels.expression' | translate }}
          </div>
          <div class="variables-editor-header variables-alias">
            {{ 'variables-editor.labels.alias' | translate }}
          </div>
        </div>

        <div class="variables-editor-headers" *ngSwitchCase="'user_variables'" [hidden]="items.length === 0">
          <div class="variables-editor-header variables-variable">
            {{ 'variables-editor.labels.variable' | translate }}
          </div>
          <div class="variables-editor-header variables-expression">
            {{ 'variables-editor.labels.expression' | translate }}
          </div>
        </div>
        <div class="variables-editor-headers" *ngSwitchCase="'system_variables'" [hidden]="items.length === 0">
          <div class="variables-editor-header variables-variable">
            {{ 'variables-editor.labels.variable' | translate }}
          </div>
          <div class="variables-editor-header variables-expression">
            {{ 'variables-editor.labels.expression' | translate }}
          </div>
        </div>
        <div class="variables-editor-headers" *ngSwitchCase="'cube'">
          <div class="variables-editor-header cube-type">
            {{ 'variables-editor.labels.cube-type' | translate }}
          </div>
          <div class="variables-editor-header cube-fields">
            {{ 'variables-editor.labels.cube-fields' | translate }}
          </div>
        </div>
        <div class="variables-editor-headers" *ngSwitchCase="'sort'" [hidden]="items.length === 0">
          <div class="variables-editor-header sort-by">
            {{ 'variables-editor.labels.sort-by' | translate }}
          </div>
          <div class="variables-editor-header sort-direction">
            {{ 'variables-editor.labels.sort-direction' | translate }}
          </div>
        </div>
        <div class="variables-editor-headers" *ngSwitchCase="'partition'" [hidden]="items.length === 0">
          <div class="variables-editor-header partition-by">
            {{ 'variables-editor.labels.partition-by' | translate }}
          </div>
        </div>
        <div
          class="variables-editor-headers variables-editor-headers-schema"
          *ngSwitchCase="'schema'"
          [hidden]="items.length > 0"
        >
          <div class="variables-editor-header schema-name">
            {{ 'variables-editor.labels.name' | translate }}
          </div>
          <div class="variables-editor-header schema-alias">
            {{ 'variables-editor.labels.alias' | translate }}
          </div>
          <div class="variables-editor-header schema-datatype">
            {{ 'variables-editor.labels.datatype' | translate }}
          </div>
        </div>
      </div>
      <div class="variables-editor-sortable" cdkDropList (cdkDropListDropped)="drop($event)">
        <variables-editor-row
          *ngFor="let item of items; trackBy: identify; let i = index"
          [item]="item"
          [disableKey]="disableKey"
          [readonlyValue]="readonlyValue"
          [hideRemove]="hideRemove"
          [showUndo]="showUndo"
          [usePackageDefault]="usePackageDefault"
          [disableSort]="disableSort"
          [key]="key"
          [value]="value"
          [index]="i"
          [type]="type"
          (itemChange)="onValueChange($event, i)"
          (itemRemove)="onValueRemove($event, i)"
        ></variables-editor-row>
      </div>
      <div class="variables-editor-empty-text" *ngIf="items.length == 0">
        <h4>{{ emptyMessage | translate }}</h4>
      </div>
      <div class="variables-editor-row variables-editor-add" *ngIf="!hideAdd">
        <button type="button" class="btn btn-block btn-lg" (click)="add()" tabindex="-1">
          <svg version="1.1" xmlns="http://www.w3.org/2000/svg" width="7px" height="7px" viewBox="0 0 7 7">
            <path style="fill: #3f3f3f;" d="M4,7H3V4H0V3h3V0h1v3h3v1H4V7z" />
          </svg>
          <span *ngIf="items.length == 0">{{ 'variables-editor.labels.add_new' | translate }}</span>
          <span *ngIf="items.length > 0">{{ 'variables-editor.labels.add_another' | translate }}</span>
        </button>
      </div>
    </div>
  `,
})
export class VariablesEditorComponent {
  @Input() items: any[];
  @Input() key = 'key';
  @Input() value = 'value';
  @Input() type = '';
  @Input() hideRemove = false;
  @Input() hideAdd = false;
  @Input() showUndo = false;
  @Input() disableKey = false;
  @Input() disableSort = false;
  @Input() readonlyValue = false;
  @Input() usePackageDefault = false;
  @Input() emptyMessage = 'variables-editor.labels.empty-list';
  @Output() variablesChange = new EventEmitter();

  onValueChange(item, index: number) {
    this.items[index] = item;

    this.variablesChange.emit(this.items);
  }

  onValueRemove(item, index: number) {
    this.items = this.items.filter((_, i) => index !== i);

    this.variablesChange.emit(this.items);
  }

  drop(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.items, event.previousIndex, event.currentIndex);
  }

  // eslint-disable-next-line class-methods-use-this
  identify(index, item) {
    return this.hideRemove ? item.key : index;
  }

  add() {
    const newItem: any = {};

    newItem[this.key] = '';
    newItem[this.value] = '';
    newItem.data_type = 'string';

    this.items = this.items.concat(newItem);

    this.variablesChange.emit(this.items);
  }
}
